import { motion } from 'framer-motion'
import { Award, BookOpen, Info, TrendingUp, Users, type LucideIcon } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useLmsHealth } from '@/services/admin/metrics.service'
import { cn } from '@/lib/utils'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

interface KpiSpec {
  label: string
  value: string | number
  hint?: string
  /** Texto explicativo da métrica — mostrado em tooltip no hover do ícone Info. */
  explanation: string
  icon: LucideIcon
  /** Tailwind class do gradiente do ícone */
  iconColor: string
  /** Gradiente sutil de fundo do card */
  bgGradient: string
}

/**
 * 4 cards hero com KPIs principais do LMS. Layout grid responsivo:
 * 1 col mobile, 2 cols tablet, 4 cols desktop.
 *
 * Animação stagger via Framer Motion — cada card entra com pequeno delay.
 */
export function HeroKpis({ filters }: Props) {
  const { data, isLoading, isError } = useLmsHealth(filters.companyIds)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Não foi possível carregar os indicadores. Tente recarregar.
        </CardContent>
      </Card>
    )
  }

  const kpis: KpiSpec[] = [
    {
      label: 'Usuários ativos',
      value: data.users.active30d,
      hint: `${data.users.total} cadastrados · +${data.users.new7d} em 7d`,
      explanation:
        'Usuários que tiveram QUALQUER atividade (assistir episódio ou responder quiz) nos últimos 30 dias. Não inclui apenas login — precisa interagir com conteúdo. "Cadastrados" é o total histórico de contas. "+N em 7d" são contas criadas na última semana.',
      icon: Users,
      iconColor: 'text-sky-400',
      bgGradient: 'from-sky-500/10 to-sky-500/0',
    },
    {
      label: 'Conclusões de curso',
      value: data.enrollment.completed,
      hint: `${data.enrollment.completionRate.toFixed(1)}% de taxa · ${data.enrollment.inProgress} em andamento`,
      explanation:
        'Cursos completados (todos os episódios assistidos pelo aluno). Taxa = completados ÷ total de matrículas × 100. "Em andamento" são matrículas com pelo menos 1 episódio iniciado mas curso ainda não finalizado. O filtro de empresa restringe a quem está vinculado a ela.',
      icon: BookOpen,
      iconColor: 'text-emerald-400',
      bgGradient: 'from-emerald-500/10 to-emerald-500/0',
    },
    {
      label: 'Badges concedidas',
      value: data.badges.totalGrants,
      hint: `${data.badges.distinctUsers} usuários · ${data.badges.avgPerUser.toFixed(1)} por user`,
      explanation:
        'Total de conquistas concedidas (cada user × badge × ciclo conta como 1 grant). "N usuários" = quantos receberam pelo menos 1 badge. "Avg por user" = média de badges entre os que ganharam alguma — usuários sem nenhuma badge NÃO entram na conta.',
      icon: Award,
      iconColor: 'text-amber-400',
      bgGradient: 'from-amber-500/10 to-amber-500/0',
    },
    {
      label: 'Engajamento',
      value: `${data.engagement.averageQuizScore.toFixed(0)}%`,
      hint: `${data.engagement.episodesCompletedTotal} eps · ${data.engagement.quizzesCompletedTotal} quizzes · ★${data.engagement.averageRating.toFixed(1)}`,
      explanation:
        'Nota média global em quizzes (média ponderada de TODAS as tentativas concluídas, não só a 1ª). Os outros números são totais absolutos no escopo filtrado: episódios concluídos no histórico, quizzes respondidos e média de avaliação ★ dada pelos alunos.',
      icon: TrendingUp,
      iconColor: 'text-primary',
      bgGradient: 'from-primary/15 to-amber-400/5',
    },
  ]

  return (
    <TooltipProvider delayDuration={150}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, idx) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.08 }}
          >
            <Card
              className={cn(
                'glass-card border-border/50 overflow-hidden relative bg-gradient-to-br',
                k.bgGradient
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        {k.label}
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground/60 hover:text-foreground transition-colors"
                            aria-label={`Como ${k.label} é calculada`}
                          >
                            <Info className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-[280px] text-xs leading-relaxed"
                        >
                          {k.explanation}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-3xl font-bold tabular-nums mt-2">{k.value}</p>
                    {k.hint && (
                      <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                        {k.hint}
                      </p>
                    )}
                  </div>
                  <div
                    className={cn(
                      'rounded-xl p-2.5 bg-background/40 backdrop-blur-sm',
                      k.iconColor
                    )}
                  >
                    <k.icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </TooltipProvider>
  )
}
